import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { LxConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { WizardValidationState } from "../validations/WizardValidationState";
import { ValidationData, ValidationState } from "../validations/typings";

export class LxWizardState implements LOBState {
  @computed
  public get location() {
    return this.globalState.location;
  }

  private globalState: GlobalWizardState;

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @observable
  public config: LxConfig;

  @observable
  public subLOBState = "";

  public moduleName = "wizard-lx-pwa";

  @observable
  public trackingEnabled = true;

  public validations: ValidationState;

  @observable
  public destinationInvalidKey: string | "";

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  @observable
  public numberOfErrors = 0;

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @computed
  public get date() {
    return this.globalState.date;
  }

  public setDefaultDestination = (
    destinationValue: string,
    regionID: string,
    countryCode: string,
    ttla: string,
    destinationID: string,
    hotelID: string
  ) => {
    this.globalState.updateDestinationFromContext(
      destinationValue,
      regionID,
      countryCode,
      "",
      ttla,
      destinationID,
      hotelID
    );
    this.destinationInvalidKey = "";
  };

  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  /**
   * Validations
   */

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @action
  public cleanErrorState = () => {
    this.errorInputRef = null;
    this.numberOfErrors = 0;
  };

  public updateStateValidations = (validationsData: any) => {
    this.updateLocErrorKeys(validationsData.invalidKeys);
    this.errorInputRef = validationsData.inputReference;
    this.numberOfErrors = validationsData.numberOfErrors;
  };

  public updateLocErrorKeys = (invalidKeys: any) => {
    this.dateEndInvalidKey = invalidKeys.dateEnd;
    this.destinationInvalidKey = invalidKeys.destination;
  };

  public submit = (event: React.FormEvent) => {
    const dataToValidate: ValidationData = {
      location: {
        origin: null,
        destination: this.location.destination.value,
      },
      dates: {
        start: this.date.start,
        end: this.date.end,
      },
      references: this.wizardInputsArray,
    };

    const { isValid, data } = this.validations.validateForm(dataToValidate, this.config);

    if (!isValid) {
      this.updateStateValidations(data);
      event.preventDefault();
      this.validations.focusErrorSummary();
    }
  };

  constructor(config: LxConfig, globalState: GlobalWizardState) {
    this.config = config;
    this.globalState = globalState;
    this.validations = new WizardValidationState();
  }
}
